import { stardust } from '@eidellev/adonis-stardust/client';
import Pristine from './pristine';
import { getPageLang } from './utils';

/*
 * Contact page redesign (contact-us-new) — inquiry-form helpers:
 * - "Contact this boutique" card CTAs ([data-set-location]) pre-select that
 *   boutique in the inquiry form, then scroll to and focus the select
 * - Quick topic buttons ([data-topic-quick]) set the topic select and mirror
 *   the selection back with aria-pressed checkmarks
 * - The routing note under the boutique select shows which inbox the
 *   inquiry goes to (template + per-option email come from the markup)
 * This form owns its submission and confirmation; other forms keep forms.ts.
 */

document.addEventListener('DOMContentLoaded', () => {
  const form = document.getElementById('inquiry-form') as HTMLFormElement | null;
  if (!form) {
    return;
  }

  const locationSelect = form.querySelector<HTMLSelectElement>('#iq-location');
  const topicSelect = form.querySelector<HTMLSelectElement>('#iq-topic');
  const routingNote = document.getElementById('iq-routing-note');
  const emptyNote = routingNote ? routingNote.textContent : '';
  const fields = form.querySelector<HTMLElement>('[data-inquiry-fields]');
  const confirmation = form.querySelector<HTMLElement>('[data-inquiry-success]');
  const errorNotice = form.querySelector<HTMLElement>('[data-inquiry-error]');
  const submitButton = form.querySelector<HTMLButtonElement>('[type="submit"]');
  const submitLabel = submitButton?.textContent || '';
  let sending = false;
  let sent = false;

  function reveal(target: HTMLElement) {
    target.hidden = false;
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: 'auto', block: 'center' });
  }

  // Only the opted-in Contact Us form uses this handler. The legacy form
  // helpers and dedicated Rolex/TAG forms remain unchanged.
  if (form.hasAttribute('data-contact-inquiry') && fields && confirmation && errorNotice && submitButton) {
    const inquiryFields = fields;
    const successPanel = confirmation;
    const errorPanel = errorNotice;
    const button = submitButton;
    const pristine = new Pristine(form, {
      classTo: 'form__input',
      errorTextParent: 'form__input',
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (sending || sent) {
        return;
      }
      errorPanel.hidden = true;
      if (!pristine.validate()) {
        form.querySelector<HTMLElement>('.has-danger input, .has-danger select, .has-danger textarea')?.focus();
        return;
      }

      // Capture the selected boutique and all field values before disabling
      // controls. Disabled controls are omitted by FormData.
      const data = new FormData(form);
      data.append('lang', getPageLang());
      const controls = Array.from(form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>('input, select, textarea, button'));
      const disabledBefore = controls.map((control) => control.disabled);
      sending = true;
      form.setAttribute('aria-busy', 'true');
      controls.forEach((control) => { control.disabled = true; });
      button.textContent = form.dataset.sendingLabel || submitLabel;

      try {
        const response = await fetch(stardust.route(form.dataset.route || ''), {
          method: 'POST',
          credentials: 'same-origin',
          body: data,
        });
        const result = await response.json();
        // A delivered email can be followed by an integration failure. Show
        // success only after the existing endpoint confirms the submission.
        if (!response.ok || result?.message !== 'email sent') {
          throw new Error('Inquiry was not confirmed');
        }
        sent = true;
        inquiryFields.hidden = true;
      } catch {
        // Preserve the customer's details. The localized notice explains that
        // delivery is uncertain, without encouraging a duplicate submission.
        controls.forEach((control, index) => { control.disabled = disabledBefore[index]; });
        button.textContent = submitLabel;
      } finally {
        sending = false;
        form.removeAttribute('aria-busy');
        reveal(sent ? successPanel : errorPanel);
      }
    });
  }

  function updateRoutingNote() {
    if (!routingNote || !locationSelect) {
      return;
    }
    const option = locationSelect.selectedOptions[0];
    const email = option ? option.getAttribute('data-email') : '';
    const template = locationSelect.getAttribute('data-routing-template') || '';
    routingNote.textContent =
      locationSelect.value && email ? template.replace('{email}', email) : emptyNote;
  }

  function updateTopicButtons() {
    document.querySelectorAll<HTMLButtonElement>('[data-topic-quick]').forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        String(!!topicSelect && button.dataset.topicQuick === topicSelect.value),
      );
    });
  }

  function jumpToForm(focusTarget: HTMLElement | null) {
    if (sent && confirmation) {
      reveal(confirmation);
      return;
    }
    form!.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (focusTarget) {
      focusTarget.focus({ preventScroll: true });
    }
  }

  document.querySelectorAll<HTMLElement>('[data-set-location]').forEach((link) => {
    link.addEventListener('click', (e) => {
      if (!locationSelect) {
        return;
      }
      e.preventDefault();
      if (sending || sent) {
        if (sent && confirmation) reveal(confirmation);
        return;
      }
      locationSelect.value = link.dataset.setLocation || '';
      updateRoutingNote();
      jumpToForm(locationSelect);
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-topic-quick]').forEach((button) => {
    button.addEventListener('click', () => {
      if (sending || sent) {
        if (sent && confirmation) reveal(confirmation);
        return;
      }
      if (!topicSelect) {
        return;
      }
      topicSelect.value = button.dataset.topicQuick || '';
      updateTopicButtons();
      jumpToForm(locationSelect);
    });
  });

  locationSelect?.addEventListener('change', updateRoutingNote);
  topicSelect?.addEventListener('change', updateTopicButtons);

  updateRoutingNote();
  updateTopicButtons();
});
